import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Car, Package, Clock, QrCode, Search } from "lucide-react";

export default function QuickActions() {
  const navigate = useNavigate();
  const [showCheckout, setShowCheckout] = useState(false);
  const [qrSearch, setQrSearch] = useState("");
  const [checkoutResult, setCheckoutResult] = useState(null);
  const { location } = useAuth();

  const actions = [
    { label: "New Visitor", icon: Users, path: "/mod1", state: { openForm: true, tab: "visitor" } },
    { label: "New Vehicle", icon: Car, path: "/mod5", state: { openForm: true } },
    { label: "New RGP", icon: Package, path: "/mod2", state: { openForm: true, tab: "rgp" } },
    { label: "Employee Out", icon: Clock, path: "/mod1", state: { openForm: true, tab: "employee" } },
  ];

  const handleQRCheckout = async () => {
    if (!qrSearch.trim()) return;
    try {
      const { data } = await api.get("/gatepass/lookup", { params: { qr_data: qrSearch } });
      setCheckoutResult(data.visitor);
    } catch {
      toast.error("Gate pass not found");
      setCheckoutResult(null);
    }
  };

  const confirmCheckout = async () => {
    if (!checkoutResult) return;
    try {
      await api.put(`/mod1/visitor/${checkoutResult.id}/checkout`);
      toast.success(`${checkoutResult.visitor_name} checked out`);
      setShowCheckout(false);
      setCheckoutResult(null);
      setQrSearch("");
    } catch { toast.error("Checkout failed"); }
  };

  return (
    <>
      <div className="flex items-center gap-1 sm:gap-1.5 whitespace-nowrap" data-testid="quick-actions">
        {actions.map((a) => (
          <Button
            key={a.label}
            variant="ghost"
            size="sm"
            className="h-7 sm:h-8 text-[11px] sm:text-xs gap-1 sm:gap-1.5 text-gray-600 hover:text-[#1B3C87] hover:bg-[#1B3C87]/5 shrink-0 px-2 sm:px-3"
            onClick={() => navigate(a.path, { state: a.state })}
            data-testid={`quick-${a.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <a.icon size={14} /> <span className="hidden sm:inline">{a.label}</span><span className="sm:hidden">{a.label.split(" ")[0]}</span>
          </Button>
        ))}
        <div className="w-px h-5 bg-gray-200 mx-0.5 sm:mx-1 shrink-0" />
        <Button
          variant="ghost"
          size="sm"
          className="h-7 sm:h-8 text-[11px] sm:text-xs gap-1 sm:gap-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 shrink-0 px-2 sm:px-3"
          onClick={() => setShowCheckout(true)}
          data-testid="quick-checkout"
        >
          <QrCode size={14} /> <span className="hidden sm:inline">Quick Checkout</span><span className="sm:hidden">Checkout</span>
        </Button>
      </div>

      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent data-testid="quick-checkout-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }}>Quick Checkout — Scan or Search</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Gate Pass No. or QR data..."
                  value={qrSearch}
                  onChange={(e) => setQrSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleQRCheckout()}
                  className="pl-9 h-11"
                  data-testid="qr-search-input"
                />
              </div>
              <Button onClick={handleQRCheckout} className="bg-[#1B3C87] h-11">Search</Button>
            </div>

            {checkoutResult && (
              <div className="p-4 border rounded bg-blue-50">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold">{checkoutResult.visitor_name}</div>
                    <div className="text-sm text-gray-500">{checkoutResult.company_from} · {checkoutResult.gate_pass_no}</div>
                    <div className="text-xs text-gray-400">In since: {new Date(checkoutResult.in_time).toLocaleString("en-IN")}</div>
                  </div>
                  <Button onClick={confirmCheckout} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-checkout-btn">
                    Checkout
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
